import React, { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { DrawerHeader } from "@/components/ui/drawer";
import { CaretLeft } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Step } from "../expense-form/constants";
import { VENDOR_PAYMENT_DETAILS_STEP } from "./constants";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { VendorPaymentDetailsFormValues } from ".";
import { useTranslation } from "react-i18next";

const InitialStep = ({
  form,
  setStep,
  setVendorPaymentDetailStep,
}: {
  form: UseFormReturn<VendorPaymentDetailsFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>; // General step
  setVendorPaymentDetailStep: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { t } = useTranslation();

  return (
    <div>
      <DrawerHeader
        title={t("title.add_vendor_payment_methods")}
        description={t("description.add_vendor_payment_methods")}
        leftButton={
          <button type="button" onClick={() => setStep(Step.INITAL)}>
            <CaretLeft color="#E3EDFC" size={18} />
          </button>
        }
        rightButton={<span className="w-[18px]" />}
      />
      <div className="space-y-5">
        {form
          .watch("vendorPaymentMethods")
          ?.map((paymentDetail, index) => (
            <CollapsiblePaymentMethod
              key={index}
              paymentDetail={paymentDetail}
              form={form}
              index={index}
            />
          ))}
      </div>
      <Button
        data-testid="add-vendor-payment-details-add-new-payment-method-button"
        className="mt-5 w-full"
        onClick={() =>
          setVendorPaymentDetailStep(VENDOR_PAYMENT_DETAILS_STEP.PAYMENT_METHOD)
        }
      >
        {t("button.add_new_payment_method")}
      </Button>
    </div>
  );
};

const CollapsiblePaymentMethod = ({
  paymentDetail,
  form,
  index,
}: {
  paymentDetail: NonNullable<
    VendorPaymentDetailsFormValues["vendorPaymentMethods"]
  >[number];
  form: UseFormReturn<VendorPaymentDetailsFormValues>;
  index: number;
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      data-testid="add-vendor-payment-details-collapsible-payment-method"
      className={cn(
        "flex h-10 w-full flex-col overflow-hidden rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] text-white shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg transition-all duration-300",
        isOpen && "h-[auto]"
      )}
    >
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="mb-1 flex items-center justify-between"
      >
        <div className="flex items-center gap-2 text-base">
          <span className="font-medium">{paymentDetail.bankName}</span>
          <span>/</span>
          <span className="text-sm">{paymentDetail.iban}</span>
        </div>

        <div className="flex items-center gap-4">
          <span>
            {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </span>

          <button
            data-testid="add-vendor-payment-details-delete-payment-method-button"
            onClick={() => {
              form.setValue(
                "vendorPaymentMethods",
                form
                  .getValues("vendorPaymentMethods")
                  ?.filter((_, i) => i !== index)
              );
            }}
          >
            <X size={16} />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-x-2 gap-y-1 sm:grid-cols-2">
        {paymentDetail.accountHolderName && (
          <div>
            {t("label.account_holder_name")}: {paymentDetail.accountHolderName}
          </div>
        )}
        {paymentDetail.swiftCode && (
          <div>
            {t("label.swift_code")}: {paymentDetail.swiftCode}
          </div>
        )}
        {paymentDetail.bankAccountNumber && (
          <div>
            {t("label.bank_account_number")}: {paymentDetail.bankAccountNumber}
          </div>
        )}
        {paymentDetail.currencyId && (
          <div>
            {t("label.currency")}: {paymentDetail.currencyId}
          </div>
        )}
        {paymentDetail.bankAddress && (
          <div>
            {t("label.bank_address")}: {paymentDetail.bankAddress}
          </div>
        )}
        {paymentDetail.paymentTerms && (
          <div>
            {t("label.payment_terms")}: {paymentDetail.paymentTerms}
          </div>
        )}
      </div>
    </div>
  );
};

export default InitialStep;
